import { useEffect, useState } from 'react';
import {
  makeStyles, tokens, Button, Text, Spinner,
  MessageBar, MessageBarBody, MessageBarTitle,
} from '@fluentui/react-components';
import { Play20Regular } from '@fluentui/react-icons';
import { useApi } from '../api/context';
import type { Tenant, RunDetail } from '../api/client';

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS },
  row: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

/**
 * Starts a collection from the browser.
 *
 * The request returns as soon as the collector has been launched, not when it
 * finishes. A collection takes minutes and spawns child processes for Exchange
 * and Teams, so holding the request open would time out long before the work
 * was done. Progress is polled instead.
 */
export function RunScan({ tenant, onFinished }: { tenant: Tenant; onFinished: () => void }) {
  const styles = useStyles();
  const api = useApi();
  const [runId, setRunId] = useState<string | null>(null);
  const [run, setRun] = useState<RunDetail | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Poll while the collection is running. Stops as soon as it closes, so a
  // finished run is not polled indefinitely in an open tab.
  useEffect(() => {
    if (!runId) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const r = await api.getRun(runId);
        if (cancelled) return;
        setRun(r);
        if (r.status !== 'running') {
          onFinished();
          return;
        }
        setTimeout(() => void tick(), 5000);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    };
    void tick();
    return () => { cancelled = true; };
     
  }, [runId]);

  const start = async () => {
    setStarting(true); setError(null); setRun(null);
    try {
      const r = await api.startScan(tenant.tenantId);
      setRunId(r.runId);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setStarting(false);
    }
  };

  const running = run?.status === 'running';

  return (
    <div className={styles.wrap}>
      <div className={styles.row}>
        <Button
          appearance="primary"
          icon={<Play20Regular />}
          disabled={starting || running || !tenant.consentGranted}
          onClick={() => void start()}
        >
          {starting ? 'Starting' : running ? 'Collection running' : 'Run assessment'}
        </Button>
        {running && <Spinner size="tiny" />}
        {running && run?.progress && (
          <Text className={styles.muted}>{run.progress}</Text>
        )}
      </div>

      {/* Consent is the gate, and saying so is more useful than a button that
          fails when pressed. */}
      {!tenant.consentGranted && (
        <Text className={styles.muted}>
          Admin consent has not been granted for this tenant, so nothing can be collected
          from it yet.
        </Text>
      )}

      {run?.status === 'complete' && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Collection finished</MessageBarTitle>
            {run.findingCount} finding{run.findingCount === 1 ? '' : 's'} recorded.
          </MessageBarBody>
        </MessageBar>
      )}

      {run?.status === 'failed' && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Collection failed</MessageBarTitle>
            {/* A partial collection is not a clean result, so what was
                collected is not presented as an assessment. */}
            {run.progress ?? 'The collector stopped before finishing.'} Anything it did
            record is incomplete and should not be read as a full assessment.
          </MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
      )}
    </div>
  );
}
